import React from 'react';
import { ArrowLeft, ArrowRight, CheckCircle, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useDriverProfile } from '../../hooks/useDriverProfile';
import VehicleSelection from '../../components/driver-profile/VehicleSelection';
import DriverInfoStep from '../../components/driver-profile/DriverInfoStep';
import VehicleInfoStep from '../../components/driver-profile/VehicleInfoStep';
import PersonalInfoStep from '../../components/driver-profile/PersonalInfoStep';

export default function DriverProfile() {
  const navigate = useNavigate();
  const {
    // State
    currentStep,
    vehicleType,
    loading,
    saving,
    existingProfiles,
    showVehicleSelection,
    searchRaceNumber,
    searchDriverName,
    isSearching,
    searchResults,
    isOwnerSameAsDriver,
    raceNumberError,
    personalInfo,
    vehicleInfo,
    driverInfo,
    
    // Setters
    setSearchRaceNumber,
    setSearchDriverName,
    setIsOwnerSameAsDriver,
    setPersonalInfo,
    setVehicleInfo,
    setDriverInfo,
    
    // Actions
    loadProfileData,
    startNewRegistration,
    saveProgress,
    handleNext,
    handlePrevious,
    handleSubmit,
    handleSearchRaceNumber,
    handleSearchDriverName,
    selectSearchResult,
    getStepTitle
  } = useDriverProfile();

  const renderProgressBar = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-white">Driver Registration</h2>
        {!showVehicleSelection && (
          <span className="text-sm text-gray-400">Step {currentStep === 4 ? 1 : currentStep === 3 ? 2 : 3} of 3</span>
        )}
      </div>
      {!showVehicleSelection && (
        <div className="w-full bg-zinc-800 rounded-full h-2">
          <div 
            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep === 4 ? 1 : currentStep === 3 ? 2 : 3) / 3 * 100}%` }}
          />
        </div>
      )}
      <p className="text-lg text-white mt-4">{getStepTitle()}</p>
    </div>
  );

  const renderVehicleSelectionStep = () => {
    const autoProfile = existingProfiles.find(p => p.vehicleType === 'auto');
    const motorcycleProfile = existingProfiles.find(p => p.vehicleType === 'motorcycle');

    return (
      <div className="space-y-6">
        <h3 className="text-xl font-semibold text-white mb-6">Your Vehicle Registrations</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Auto Registration */}
          <div className={`p-6 rounded-lg border-2 ${
            autoProfile ? 'border-green-500 bg-green-500/10' : 'border-zinc-700'
          }`}>
            <div className="h-12 w-12 mx-auto mb-4 text-primary-500">🚗</div>
            <h4 className="text-lg font-semibold text-white text-center">Auto Registration</h4>
            
            {autoProfile ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    autoProfile.isComplete 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {autoProfile.isComplete ? 'Complete' : 'In Progress'}
                  </span>
                </div>
                {autoProfile.vehicleInfo.vehicleBrandModel && (
                  <p className="text-gray-400 text-center text-sm">
                    {autoProfile.vehicleInfo.vehicleBrandModel}
                  </p>
                )}
                {autoProfile.vehicleInfo.raceNumber && (
                  <p className="text-gray-400 text-center text-sm">
                    Race #: {autoProfile.vehicleInfo.raceNumber}
                  </p>
                )}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => loadProfileData(autoProfile)}
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded text-sm"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-gray-400 text-center mb-4">Not registered</p>
                <button
                  onClick={() => startNewRegistration('auto')}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded flex items-center justify-center gap-2"
                >
                  Register Auto
                </button>
              </div>
            )}
          </div>

          {/* Motorcycle Registration */}
          <div className={`p-6 rounded-lg border-2 ${
            motorcycleProfile ? 'border-green-500 bg-green-500/10' : 'border-zinc-700'
          }`}>
            <div className="h-12 w-12 mx-auto mb-4 text-primary-500">🏍️</div>
            <h4 className="text-lg font-semibold text-white text-center">Motorcycle Registration</h4>
            
            {motorcycleProfile ? (
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    motorcycleProfile.isComplete 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {motorcycleProfile.isComplete ? 'Complete' : 'In Progress'}
                  </span>
                </div>
                {motorcycleProfile.vehicleInfo.vehicleBrandModel && (
                  <p className="text-gray-400 text-center text-sm">
                    {motorcycleProfile.vehicleInfo.vehicleBrandModel}
                  </p>
                )}
                {motorcycleProfile.vehicleInfo.raceNumber && (
                  <p className="text-gray-400 text-center text-sm">
                    Race #: {motorcycleProfile.vehicleInfo.raceNumber}
                  </p>
                )}
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => loadProfileData(motorcycleProfile)}
                    className="flex-1 bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded text-sm"
                  >
                    Edit
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-4">
                <p className="text-gray-400 text-center mb-4">Not registered</p>
                <button
                  onClick={() => startNewRegistration('motorcycle')}
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded flex items-center justify-center gap-2"
                >
                  Register Motorcycle
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/profile')}
            className="bg-zinc-700 hover:bg-zinc-600 text-white px-6 py-3 rounded-lg"
          >
            Back to Profile
          </button>
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    if (showVehicleSelection) {
      return renderVehicleSelectionStep();
    }
    
    switch (currentStep) {
      case 4: return (
        <DriverInfoStep
          vehicleType={vehicleType}
          driverInfo={driverInfo}
          setDriverInfo={setDriverInfo}
          searchDriverName={searchDriverName}
          setSearchDriverName={setSearchDriverName}
          searchRaceNumber={searchRaceNumber}
          setSearchRaceNumber={setSearchRaceNumber}
          isSearching={isSearching}
          searchResults={searchResults}
          raceNumberError={raceNumberError}
          onSearchDriverName={handleSearchDriverName}
          onSearchRaceNumber={handleSearchRaceNumber}
          onSelectSearchResult={selectSearchResult}
        />
      );
      case 3: return (
        <VehicleInfoStep
          vehicleType={vehicleType}
          vehicleInfo={vehicleInfo}
          setVehicleInfo={setVehicleInfo}
          raceNumberError={raceNumberError}
        />
      );
      case 2: return (
        <PersonalInfoStep
          vehicleType={vehicleType}
          personalInfo={personalInfo}
          setPersonalInfo={setPersonalInfo}
          isOwnerSameAsDriver={isOwnerSameAsDriver}
          setIsOwnerSameAsDriver={setIsOwnerSameAsDriver}
        />
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-black py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {renderProgressBar()}
        
        <div className="bg-zinc-900 rounded-lg p-8">
          {renderCurrentStep()}
          
          {/* Navigation Buttons */}
          {!showVehicleSelection && (
            <div className="flex justify-between items-center mt-8 pt-6 border-t border-zinc-800">
              <div className="flex gap-4">
                <button
                  onClick={handlePrevious}
                  className="flex items-center gap-2 px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Previous
                </button>
                
                <button
                  onClick={saveProgress}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-zinc-700 hover:bg-zinc-600 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  <Save className="h-4 w-4" />
                  {saving ? 'Saving...' : 'Save Progress'}
                </button>
              </div>
              
              <div>
                {currentStep === 2 ? (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    className="flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="h-4 w-4" />
                    {loading ? 'Completing...' : 'Complete Registration'}
                  </button>
                ) : (
                  <button
                    onClick={handleNext}
                    className="flex items-center gap-2 px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors"
                  >
                    Next
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}